package ru.dinarastepina.fabvocab.pages.words.id

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import ru.dinarastepina.fabvocab.contracts.toDomain
import ru.dinarastepina.fabvocab.domain.model.WordEntry
import ru.dinarastepina.fabvocab.web.api.VocabularyApiClient
import ru.dinarastepina.fabvocab.web.components.FeedbackCard
import ru.dinarastepina.fabvocab.web.components.TopBar
import ru.dinarastepina.fabvocab.web.theme.*

@Page("/words/{id}")
@Composable
fun WordDetailPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val client = remember { VocabularyApiClient() }
    val id = ctx.route.params["id"]
    val word = remember { mutableStateOf<WordEntry?>(null) }
    val stats = remember { mutableStateOf<ru.dinarastepina.fabvocab.contracts.WordStatsDto?>(null) }
    val error = remember { mutableStateOf<String?>(null) }

    LaunchedEffect(id) {
        if (id == null) return@LaunchedEffect
        try {
            word.value = client.getWord(id)?.toDomain()
            stats.value = client.getWordStats(id)
        } catch (e: Exception) {
            error.value = e.message
        }
    }

    Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
        TopBar(
            title = "",
            navigation = {
                Button(
                    onClick = { ctx.router.navigateTo("/") },
                    colorPalette = SlatePalette,
                    size = ButtonSize.XS,
                ) {
                    SpanText("← Back")
                }
            },
        )

        word.value?.let { w ->
            val theme = posThemeFor(w.partOfSpeech)

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(Color("#FFFFFF"))
                    .border(2.px, LineStyle.Solid, Slate100)
                    .borderRadius(24.px)
                    .padding(20.px),
            ) {
                Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            w.word,
                            modifier = Modifier
                                .fontSize(28.px)
                                .fontWeight(FontWeight.Black)
                                .color(theme.text),
                        )
                        w.partOfSpeech?.let { pos ->
                            Box(
                                modifier = Modifier
                                    .margin(left = 8.px)
                                    .backgroundColor(theme.background)
                                    .borderRadius(6.px)
                                    .padding(leftRight = 8.px, topBottom = 2.px),
                            ) {
                                SpanText(
                                    pos.lowercase(),
                                    modifier = Modifier
                                        .fontSize(10.px)
                                        .fontWeight(FontWeight.Bold)
                                        .color(theme.text)
                                        .letterSpacing(0.5.px),
                                )
                            }
                        }
                    }
                    w.phonetic?.let {
                        SpanText(
                            it,
                            modifier = Modifier
                                .fontSize(16.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Indigo600),
                        )
                    }
                }
            }

            Row(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                Button(
                    onClick = { ctx.router.navigateTo("/") },
                    colorPalette = IndigoPalette,
                    size = ButtonSize.SM,
                    modifier = Modifier.weight(1f),
                ) {
                    SpanText("Back", modifier = Modifier.fontWeight(FontWeight.Bold))
                }
                Button(
                    onClick = {
                        scope.launch {
                            try {
                                client.deleteWord(w.id)
                                ctx.router.navigateTo("/")
                            } catch (e: Exception) {
                                error.value = e.message
                            }
                        }
                    },
                    colorPalette = RosePalette,
                    size = ButtonSize.SM,
                    modifier = Modifier.weight(1f),
                ) {
                    SpanText("Delete", modifier = Modifier.fontWeight(FontWeight.Bold))
                }
            }

            if (w.definitions.isNotEmpty()) {
                SpanText(
                    "Definitions",
                    modifier = Modifier
                        .margin(top = 4.px)
                        .fontSize(12.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Slate400)
                        .letterSpacing(0.5.px),
                )
                w.definitions.forEach { def ->
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(Slate50)
                            .borderRadius(16.px)
                            .padding(14.px),
                    ) {
                        Row {
                            SpanText(
                                "•",
                                modifier = Modifier
                                    .margin(right = 8.px)
                                    .fontSize(16.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(Indigo600),
                            )
                            SpanText(
                                def,
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .color(Slate700)
                                    .lineHeight(1.5),
                            )
                        }
                    }
                }
            }

            if (w.exampleSentences.isNotEmpty()) {
                SpanText(
                    "Examples",
                    modifier = Modifier
                        .margin(top = 4.px)
                        .fontSize(12.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Slate400)
                        .letterSpacing(0.5.px),
                )
                w.exampleSentences.forEach { ex ->
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(Indigo50)
                            .borderRadius(16.px)
                            .padding(14.px),
                    ) {
                        Row {
                            SpanText(
                                "\"",
                                modifier = Modifier
                                    .margin(right = 8.px)
                                    .fontSize(16.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(Indigo600),
                            )
                            SpanText(
                                ex,
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .color(Slate700)
                                    .fontStyle(FontStyle.Italic)
                                    .lineHeight(1.5),
                            )
                        }
                    }
                }
            }

            stats.value?.let { s ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .backgroundColor(Slate50)
                        .borderRadius(16.px)
                        .padding(14.px),
                ) {
                    SpanText(
                        "Stats: ${s.correct}/${s.attempts} correct (${s.accuracyPercent}%)",
                        modifier = Modifier
                            .fontSize(14.px)
                            .fontWeight(FontWeight.SemiBold)
                            .color(Slate600),
                    )
                }
            }
        } ?: run {
            if (error.value != null) {
                FeedbackCard(message = error.value!!, isError = true)
            } else {
                Box(
                    modifier = Modifier.fillMaxWidth().padding(48.px),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        "Loading...",
                        modifier = Modifier.color(Slate500).fontSize(14.px),
                    )
                }
            }
        }
    }
}
